import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DeskTable, ElementTable, ByView } from '../../components/nucleus/Shelf';
import { UserPlus, ArrowDown } from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useGroup } from '../../contexts/GroupContext';
import { useToast } from '../../components/common';
import NucleusPage, { Verb, FindField } from '../../components/nucleus/NucleusPage';
import { Cell, Row } from '../../components/nucleus/Collection';
import { Empty } from '../../components/nucleus/Empty';
import { TitledSet } from '../../components/nucleus/SetHead';
import DataTable from '../../components/nucleus/DataTable';
import Inspector, { Band, Facts, useOpen } from '../../components/nucleus/Inspector';
import { PersonCell } from '../../components/nucleus/cells';
import { usePlace } from '../../nucleus/place';
import { loadGroup, rotateGroupKey, wrapForInvitee, noAccessRealm, refusal } from '../nucleus/PeopleGroupData';
import { realmShort } from '../nucleus/groupRealms';
import { accessContractPath } from '../groups/ContractRow';
import StatusPill from '../../components/nucleus/StatusPill';
import InviteSheet from './InviteSheet';
import RealmChoice from './RealmChoice';
import { useGrantReturn } from './peopleData';
import { leaveConfirm, leaveAsks, leaveGroup } from '../groups/groupsData';
import { PersonFace } from '../../components/nucleus/faces';
import { useConfirm } from '../../components/nucleus/actions';
import { ago, dateOf } from '../../nucleus/format';

// Two roles, and nothing else: an admin runs the group; a member does what the
// realm an admin gave them allows ("No access", "Members", "Editors" …). Every
// admin is on Full Access, so every row says a role and a realm.
const ROLE = { admin: 'Admin', member: 'Member' };
const isAdminRole = (role) => role === 'admin';
// a contract's state, as the roster says it, in the contract pill's words
const PILL = { active: 'active', paused: 'paused', expired: 'expired', revoked: 'revoked' };
const pillOf = (state) => (PILL[state] ? <StatusPill status={PILL[state]} of="contract" /> : undefined);

// A REALM PICKED IN A DIALOG (change realm, demote): each realm with what it
// allows, and a line under them on what changes. The dialog's body holds the
// pick; the act reads it when it runs.
function RealmPick({ realms, initial, onPick, groupName, note }) {
    const [id, setId] = useState(initial || '');
    return (
        <span className="people-demote">
            <span className="people-invite__label">What they can do</span>
            <RealmChoice realms={realms} value={id} groupName={groupName} onChange={(v) => { setId(v); onPick(v); }} />
            {note && <span className="people-dialog__note">{note}</span>}
        </span>
    );
}

// The realms a member may be moved onto: Full Access is where admins sit, so it
// is offered only to someone already on it.
const memberChoices = (realms, current) => realms.filter((r) => r.kind !== 'full_access' || r.id === String(current || ''));

const when = (iso) => (iso ? new Date(iso).getTime() : null);
const roleOf = (m) => ROLE[isAdminRole(m.role) ? 'admin' : 'member'];
// an admin with no realm named is on Full Access, as the inspector says
const realmOf = (m) => m.realm || (isAdminRole(m.role) ? 'Full Access' : null);

/** A group's members as a table: who, their role, their realm, when they joined.
 *  A member the find passes over stays in place, faint (`dim`). */
function MemberTable({ id, label, members, meId, dim, openId, onOpen, actionsOf }) {
    const columns = [
        { id: 'name', label: 'Name', lock: true, min: 180, grow: 2, value: (m) => m.name,
          cell: (m) => (
              <span className="people-table__who">
                  <span className="people-table__face"><PersonFace url={m.avatar} size={24} /></span>
                  <span className="people-table__name">{m.name}</span>
                  {m.id === meId && <span className="people-table__you">you</span>}
              </span>
          ) },
        { id: 'role', label: 'Role', min: 84, grow: 0, value: roleOf, cell: roleOf },
        { id: 'realm', label: 'Realm', min: 120, grow: 1.2, value: realmOf, cell: (m) => realmOf(m) || '—' },
        { id: 'joined', label: 'Joined', min: 96, grow: 0, asc: false, value: (m) => when(m.joined), cell: (m) => dateOf(m.joined) || '—' },
    ];
    return (
        <DataTable
            id={id} label={label} columns={columns} rows={members} keyOf={(m) => m.id} dimOf={dim}
            currentKey={openId} onOpen={onOpen} actionsOf={actionsOf}
        />
    );
}

// A GROUP'S PEOPLE: its members (admins first) and, for an admin, who is
// invited and not yet in. Every call is a /groups/<id>/… route.
export default function MembersView({ invitations }) {
    const { section, page, groupId, isAdmin: placeAdmin, spaceName, navigate, to } = usePlace();
    const toast = useToast();
    const { user, encKeys } = useAuth();
    const { refreshGroups, switchToPersonal } = useGroup();
    const meId = String(user?.id || user?.user_id || '');
    const [d, setD] = useState(null);
    const [openId, open, close] = useOpen();
    const [find, setFind] = useState('');
    const [inviting, setInviting] = useState(false);
    const confirm = useConfirm();

    const load = useCallback(() => loadGroup(groupId).then(setD), [groupId]);
    useEffect(() => {
        let live = true;
        loadGroup(groupId).then((x) => { if (live) setD(x); });
        return () => { live = false; };
    }, [groupId]);
    useGrantReturn();

    const admin = d ? d.admin : placeAdmin;
    const name = d?.group?.name || spaceName;
    const adminHint = `Only an admin of ${name} can`;
    const memberIds = useMemo(() => new Set((d?.members || []).map((m) => m.id)), [d]);
    const pendingNames = useMemo(() => new Set((d?.pending || []).map((i) => i.name)), [d]);

    const invite = async (uid, username, role = 'member', realmId = null) => {
        const enc = await wrapForInvitee(groupId, uid, encKeys?.privateKey);
        const realm = (d?.realms || []).find((r) => r.id === realmId);
        try {
            await api.inviteToGroup(groupId, { username, role, ...(realmId ? { privacy_realm_id: realmId } : {}), ...(enc ? { enc_group_privkey: enc } : {}) });
            toast.success(`Invited ${username}${role === 'admin' ? ' as an admin' : realm ? ` on ${realm.name}` : ''}${enc ? '' : '. They get the group key on the next re-key.'}`);
            load();
            return true;
        } catch (e) {
            toast.error(refusal(e, `Could not invite ${username}`));
            return false;
        }
    };

    // EVERY ACT ON ONE MEMBER — the inspector's bar and the cell's right-click.
    // One primary first, then the rest, the destructive one last.
    const memberActions = (m) => {
        if (m.id === meId) {
            return [{
                id: 'share', label: 'Edit what you share', primary: true, hint: `What you share with ${name}`,
                run: () => navigate(to('self', 'share')),
            }, {
                id: 'leave', label: 'Leave group', danger: true,
                confirm: leaveAsks({ role: m.role, members: d?.members, meId }) ? leaveConfirm({ name }) : undefined,
                run: async () => {
                    if (!(await leaveGroup(groupId, meId, confirm, { name, role: m.role, members: d?.members }))) return;
                    toast.success(`You left ${name}`);
                    switchToPersonal?.();
                    refreshGroups?.();
                    navigate('/groups');
                },
            }];
        }
        // in a group, a member card is about the group: no friending or
        // following from here (People in your own space does that). Every act
        // on another member is an admin's: a member sees none, not greyed ones.
        if (!admin) return [];
        return [
            !isAdminRole(m.role) && realmAction(m),
            !isAdminRole(m.role) && {
                id: 'promote', label: 'Make admin', hint: 'Make them an admin of this group',
                confirm: {
                    title: `Make ${m.name} an admin of ${name}?`,
                    body: `They will run ${name}: invite and remove people, change its realms, contracts, apps and settings, and delete it. Their realm moves ${m.realm ? `from ${m.realm} ` : ''}to Full Access. What they share with ${name} does not change.`,
                    label: 'Make admin',
                },
                run: async () => { await api.promoteGroupMember(groupId, m.id); toast.success(`${m.name} is an admin`); load(); },
            },
            isAdminRole(m.role) && demoteAction(m),
            {
                id: 'remove', label: 'Remove', danger: true,
                confirm: {
                    title: `Remove ${m.name} from ${name}?`,
                    body: `Their membership ends, and both contracts with it: what they share with ${name}, and what they can do in it${m.realm ? ` (${m.realm})` : ''}. ${name} gets a fresh key so they cannot read what comes next.`,
                    label: 'Remove',
                },
                run: async () => {
                    await api.removeGroupMember(groupId, m.id);
                    toast.success(`${m.name} was removed`);
                    close();
                    load();
                    // FORWARD SECRECY: a fresh key for everyone who stays. A failure
                    // never undoes the removal.
                    try {
                        const r = await rotateGroupKey(groupId, { ifEncrypted: true });
                        if (r) toast.success('Group key rotated. They cannot read anything new.');
                    } catch {
                        toast.error('Removed, but the re-key failed. Re-key from Settings to finish.');
                    }
                },
            },
        ].filter(Boolean);
    };

    // WHAT A MEMBER CAN DO is changed on purpose, in a dialog that says what each
    // realm allows — never by a select that acts the moment it changes
    function realmAction(m) {
        const realms = memberChoices(d?.realms || [], m.realmId);
        const pick = { id: m.realmId ? String(m.realmId) : '' };
        return {
            id: 'realm', label: 'Edit access', primary: true, hint: 'What they can do with this group’s data',
            confirm: {
                title: `Change what ${m.name} can do in ${name}?`,
                body: <RealmPick realms={realms} initial={pick.id} groupName={name} onPick={(id) => { pick.id = id; }}
                    note={`This is their “What they can do in ${name}” contract. Their own share is separate and stays theirs.`} />,
                label: 'Save',
            },
            run: async () => {
                const r = realms.find((x) => x.id === pick.id);
                // a member with no realm yet starts with none picked: say so, not nothing
                if (!r) throw new Error(`Pick a realm for ${m.name} first.`);
                if (r.id === String(m.realmId || '')) return;
                try {
                    await api.updateGroupMember(groupId, m.id, { privacy_realm_id: r.id });
                } catch (e) { throw new Error(refusal(e, 'Could not change what they can do')); }
                toast.success(`${m.name} is on ${r.name}`);
                load();
            },
        };
    }

    // a demoted admin lands on a realm: No access unless the admin picks another
    function demoteAction(m) {
        const realms = memberChoices(d?.realms || []);
        const pick = { id: noAccessRealm(realms)?.id || '' };
        return {
            // a demotion is never the lead act: an admin's panel may have none
            id: 'demote', label: 'Make member', icon: <ArrowDown />, hint: 'Make them a member of this group',
            confirm: {
                title: `Make ${m.name} a member of ${name}?`,
                body: realms.length ? (
                    <RealmPick realms={realms} initial={pick.id} groupName={name} onPick={(id) => { pick.id = id; }}
                        note={`They stop running ${name}. Their realm decides what they can do with its data: No access until you pick another. What they share with ${name} does not change.`} />
                ) : undefined,
                label: 'Make member',
            },
            // the server refuses the last admin, and says so
            run: async () => {
                const realm = realms.find((r) => r.id === pick.id);
                try {
                    await api.request(`/groups/${groupId}/members/${m.id}/demote`, { method: 'POST', ...(pick.id ? { body: { privacy_realm_id: pick.id } } : {}) });
                } catch (e) { throw new Error(refusal(e, `Could not demote ${m.name}`)); }
                toast.success(realm ? `${m.name} is a member, on ${realm.name}` : `${m.name} is a member`);
                load();
            },
        };
    }

    const inviteActions = (i) => [{
        id: 'cancel', label: 'Cancel invitation', danger: true, disabled: !admin, hint: admin ? undefined : `${adminHint} cancel an invitation`,
        confirm: { title: `Cancel the invitation to ${i.name}?`, body: `It stops working at once, including a join ${i.name} has started. You can invite them again.`, label: 'Cancel invitation' },
        run: async () => { await api.cancelInvitation(groupId, i.id); toast.success(`Invitation to ${i.name} cancelled`); close(); load(); },
    }];

    const inviteVerb = (
        <Verb primary icon={<UserPlus />} disabled={!admin} title={admin ? undefined : `${adminHint} invite`} onClick={() => setInviting(true)}>Invite</Verb>
    );
    const sheet = <InviteSheet open={inviting} onClose={() => setInviting(false)} memberIds={memberIds} pendingNames={pendingNames} realms={d?.realms || []} groupName={name} onInvite={invite} />;

    if (invitations) {
        const pending = d?.pending || [];
        const current = pending.find((i) => i.id === String(openId));
        return (
            <NucleusPage
                section={section.label}
                title={page.label}
                count={d && admin ? pending.length : undefined}
                verbs={inviteVerb}
                inspector={current && <SentInvitation key={current.id} i={current} meId={meId} actions={inviteActions(current)} onClose={close} />}
            >
                {d === null ? null : !admin ? (
                    <Empty>Only an admin of {name} sees who is invited.</Empty>
                ) : (
                    <DeskTable top={0} between={40}>
                        <ElementTable heads={{ note: 'Invitation', tag: 'Status' }} title="Waiting to join" count={pending.length} empty="No invitations waiting. Invite finds anyone on ofself.">
                            {pending.map((i) => (
                                <Cell key={i.id} actions={inviteActions(i)}>
                                    <PersonCell
                                        person={{ name: i.name }}
                                        note={[i.role === 'admin' ? 'Admin' : i.realm, `invited ${ago(i.at, { dateAfter: 30 })}${i.by ? ` by ${i.by}` : ''}`].filter(Boolean).join(' · ')}
                                        tag="WAITING"
                                        current={i.id === String(openId)}
                                        onPress={() => open(i.id)}
                                    />
                                </Cell>
                            ))}
                        </ElementTable>
                    </DeskTable>
                )}
                {sheet}
            </NucleusPage>
        );
    }

    const q = find.trim().toLowerCase();
    const members = d?.members || [];
    const admins = members.filter((m) => isAdminRole(m.role));
    const rest = members.filter((m) => !isAdminRole(m.role));
    const current = members.find((m) => m.id === String(openId));
    const dim = (m) => !!q && !m.name.toLowerCase().includes(q);
    const cell = (m) => (
        <Cell key={m.id} actions={memberActions(m)}>
            <PersonCell
                person={{ ...m, role: isAdminRole(m.role) ? 'admin' : 'member', realm: null }}
                tag={isAdminRole(m.role) ? 'ADMIN' : undefined}
                note={[m.realm, m.id === meId && 'you'].filter(Boolean).join(' · ') || undefined}
                dim={dim(m)}
                current={m.id === String(openId)}
                title={isAdminRole(m.role) ? `${m.name} runs ${name}, on ${m.realm || 'Full Access'}`
                    : m.realm ? `${m.name} can do what ${m.realm} allows` : m.name}
                onPress={() => open(m.id)}
            />
        </Cell>
    );
    // admins first, then everyone else: as cards or as a table, one head for both
    const sets = [
        { id: 'group-admins', title: 'Admins', members: admins, count: d ? admins.length : undefined, empty: d ? 'No admins.' : undefined },
        { id: 'group-members', title: 'Members', members: rest, count: d ? rest.length : undefined, empty: d ? 'Nobody else here yet. Invite finds anyone on ofself.' : undefined },
    ];

    return (
        <NucleusPage
            section={section.label}
            title={page.label}
            count={d ? members.length : undefined}
            verbs={<><FindField value={find} onChange={setFind} placeholder="Find a member" />{inviteVerb}</>}
            inspector={current && (
                <MemberInspector
                    key={current.id}
                    m={current}
                    self={current.id === meId}
                    d={d}
                    name={name}
                    actions={memberActions(current)}
                    onClose={close}
                    to={to}
                    navigate={navigate}
                    groupId={groupId}
                />
            )}
        >
            {d && !d.group ? (
                <Empty retry={load}>Could not read this group.</Empty>
            ) : (
                <ByView
                    cards={(
                        <DeskTable top={0} between={40}>
                            {sets.map((s) => <ElementTable key={s.id} title={s.title} count={s.count} empty={s.empty}>{s.members.map(cell)}</ElementTable>)}
                        </DeskTable>
                    )}
                    rows={(
                        <DeskTable top={0} between={40}>
                            {sets.map((s) => (
                                <TitledSet key={s.id} title={s.title} count={s.count} empty={s.empty}>
                                    {s.members.length > 0 && <MemberTable id={s.id} label={s.title} members={s.members} meId={meId} dim={dim} openId={openId} onOpen={open} actionsOf={memberActions} />}
                                </TitledSet>
                            ))}
                        </DeskTable>
                    )}
                />
            )}
            {sheet}
        </NucleusPage>
    );
}

// AN INVITATION YOU SENT, still waiting: who sent it and when, and what it offers.
function SentInvitation({ i, meId, actions, onClose }) {
    const canDo = i.role === 'admin' ? 'Full Access' : i.realm;
    return (
        <Inspector
            face={<PersonFace size={56} />}
            tag="Invitation · Waiting"
            title={i.name}
            sub={i.username && i.username !== i.name ? `@${i.username}` : undefined}
            actions={actions}
            onClose={onClose}
        >
            <Band title="The invitation">
                <Facts items={[
                    { label: 'Invited by', value: i.byId && i.byId === meId ? 'You' : i.by || 'An admin' },
                    { label: 'When', value: ago(i.at, { dateAfter: 30 }) || '—' },
                    { label: 'Role', value: ROLE[i.role] || 'Member' },
                    { label: 'Can do', value: canDo || '—' },
                    i.expires && { label: 'Expires', value: dateOf(i.expires) },
                ]} />
            </Band>
        </Inspector>
    );
}

// ONE MEMBER: what the group lets them do first, as the door to that contract's
// page (the Apps panel's "Its contract"); then whether they share into the
// group — yours by name, another member's only as sharing or not, never their
// realms. Who they are (@username, joined, role) is the header's, said once;
// an admin is the tag, with no band explaining it.
// Every act is in one bar — changing what they can do is one of them, asked in a dialog.
function MemberInspector({ m, self, d, name, actions, onClose, to, navigate, groupId }) {
    const realm = d.realms.find((r) => r.id === String(m.realmId));
    const role = isAdminRole(m.role) ? 'admin' : 'member';
    const realmName = m.realm || (role === 'admin' ? 'Full Access' : null);
    // Yours opens where you edit it; someone else's is theirs, a contract they
    // own, and no page of the group's shows it — so it has no Open.
    const openShare = self ? () => navigate(to('self', 'share')) : null;
    const openAccess = m.accessEpId ? () => navigate(accessContractPath(groupId, m.accessEpId)) : null;
    const meaning = realmShort(realm || { kind: role === 'admin' ? 'full_access' : null }) || realm?.description || null;
    // a pill only where the roster says how it stands, and on a share only when
    // it shares something ("Nothing yet" has none)
    const shares = self ? m.shares?.length > 0 : m.sharing;
    const shareAside = shares ? pillOf(m.shareState) : undefined;

    return (
        <Inspector
            face={<PersonFace url={m.avatar} size={56} />}
            tag={[ROLE[role], self && 'You'].filter(Boolean).join(' · ')}
            title={m.name}
            sub={[m.username && m.username !== m.name && `@${m.username}`, m.joined && `Joined ${dateOf(m.joined)}`].filter(Boolean).join(' · ') || undefined}
            actions={actions}
            onClose={onClose}
        >
            <Band title={`Can do in ${name}`} aside={pillOf(m.accessState)}>
                <Row
                    name={realmName || 'No realm'}
                    sub={meaning || undefined}
                    end={openAccess ? 'Open' : undefined}
                    onPress={openAccess || undefined}
                />
            </Band>
            {self ? (
                <Band title={`Shares into ${name}`} aside={shareAside}>
                    <Row name={shares ? m.shares.join(' + ') : 'Nothing yet'} end="Open" onPress={openShare} />
                </Band>
            ) : (
                // theirs is a contract they own, with no page here: a fact, not a door
                <Band title="Their share" aside={shareAside}>
                    <Facts items={[{ label: `Shares into ${name}`, value: shares ? 'Sharing' : 'Nothing yet' }]} />
                </Band>
            )}
        </Inspector>
    );
}
